<template>
  <div class="p-6 space-y-8">

    <!-- Welcome Header. The "System Operational" pulsing dot used to
         live here as static decoration; it's now a live, query-backed
         StatusIndicator chip in the top app bar (default layout). -->
    <div class="flex items-start justify-between gap-4">
      <div>
        <h1 class="text-3xl font-bold text-slate-800 dark:text-white">Snowflake Command Center</h1>
        <p class="text-slate-500 mt-1">System Health, Cost, and Performance Overview</p>
      </div>
      <!-- Refresh + freshness indicator. Hidden until the dashboard has
           something to show, so it doesn't appear during the setup-CTA
           or initial skeleton states. -->
      <div v-if="hasAnyData" class="flex flex-col items-end gap-1">
        <button
          @click="refreshAll"
          :disabled="refreshing"
          class="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium shadow-sm transition-colors"
        >
          <svg v-if="refreshing" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <Icon v-else name="heroicons:arrow-path" class="w-4 h-4" />
          {{ refreshing ? 'Refreshing…' : 'Refresh' }}
        </button>
        <p v-if="oldestAgeMs !== null" class="text-xs text-slate-500 dark:text-slate-400">
          Updated {{ formatRelativeAge(oldestAgeMs) }}
        </p>
      </div>
    </div>

    <!-- Filter bar — date range only on overview for now; the bar is
         opt-in per page (see useDashboardFilters) and other pages can
         enable warehouse / user / role filters as needed. -->
    <DashboardFilterBar
      v-if="setupReady && setupComplete"
      :filters="filters.state"
      :dirty="filters.isDirty.value"
      @reset="filters.reset"
    />

    <!-- Refresh banner. Renders any time data is loading, so the user
         always knows something is happening — not just during background
         refreshes. The copy switches based on whether we had cached data
         on screen when the load started: a cold first-load reads as
         "Loading…" while a warm refresh reads as "Refreshing…" with the
         "current view stays" reassurance. -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="refreshing"
        class="flex items-center gap-3 rounded-lg border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/70 dark:bg-indigo-500/10 px-4 py-2.5 text-sm text-indigo-800 dark:text-indigo-200"
      >
        <svg class="animate-spin h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <span v-if="hadDataAtRefreshStart">
          Refreshing data — current view stays until the new data is ready.
        </span>
        <span v-else>
          Loading data from Snowflake — this can take a few seconds the first time.
        </span>
      </div>
    </Transition>

    <!-- 💬 Three top-level branches:
         1. Setup incomplete → CTA card pointing back at the wizard.
            Only shown when we're certain setup isn't done; we wait
            for the account profile to hydrate so a fresh page load
            doesn't flash the CTA before reading Firestore.
         2. Setup complete + dashboard data still loading → skeleton
            scaffolding that mirrors the real layout. No spinner —
            users see the SHAPE of what's coming so they don't think
            the page crashed.
         3. Setup complete + data loaded → real dashboard. -->

    <!-- Branch 1: Setup not finished. -->
    <div
      v-if="setupReady && !setupComplete"
      class="rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700 bg-white/40 dark:bg-slate-800/30 py-20 px-8 text-center"
    >
      <div class="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
        <Icon name="heroicons:cube-transparent" class="w-7 h-7" />
      </div>
      <h3 class="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
        Pulsar dashboard is awake — Snowflake just isn't connected yet
      </h3>
      <p class="mx-auto mt-2 max-w-xl text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
        Finish the setup wizard so we can install the engine into your
        Snowflake account. Once installed, this view fills in with live
        cost analytics from your data.
      </p>
      <NuxtLink
        to="/setup"
        class="mt-6 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 px-4 py-2 text-sm font-medium text-white shadow-sm"
      >
        <Icon name="heroicons:rocket-launch" class="w-4 h-4" />
        Continue Setup
      </NuxtLink>
    </div>

    <!-- Branch 2: Setup complete but data still hydrating. Skeleton
         scaffolding. Same outer structure as the real dashboard so
         layout doesn't shift when the data lands. -->
    <div v-else-if="!healthData" class="space-y-12">
      <DashboardSkeleton />
    </div>

    <!-- Branch 3: real dashboard. -->
    <div v-else class="space-y-12">
      <!-- Section 1: System Health & Performance -->
      <section class="space-y-6">
        <div class="flex items-center gap-2 mb-4">
          <div class="h-8 w-1 bg-green-500 rounded-full"></div>
          <h2 class="text-xl font-bold text-slate-800 dark:text-white">System Health & Performance</h2>
        </div>

        <!-- KPI Row -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <!-- Connection latency / active query count are intrinsically real-time
               and ignore the filter; their subtext doesn't reference the range. -->
          <KPICard
             title="Connection Status"
             :value="healthData.status.status"
             :subtext="healthData.status.latency_ms + ' ms latency'"
             icon-name="heroicons:server-stack"
             icon-color="text-green-500"
             bg-color="bg-green-50 dark:bg-green-900/10"
          />
          <KPICard
             :title="'Credits Used (' + rangeLabel + ')'"
             :value="formatCredits(healthData.credits.total_in_range)"
             :subtext="formatCurrency(healthData.credits.estimated_cost_usd)"
             icon-name="heroicons:banknotes"
             icon-color="text-indigo-500"
             bg-color="bg-indigo-50 dark:bg-indigo-900/10"
          />
          <KPICard
             title="Active Queries"
             :value="healthData.queries.active_count"
             :subtext="formatTotalToday(healthData.queries.total_today)"
             icon-name="heroicons:bolt"
             icon-color="text-amber-500"
             bg-color="bg-amber-50 dark:bg-amber-900/10"
          />
          <KPICard
             :title="'Security Alerts (' + rangeLabel + ')'"
             :value="healthData.security.critical_alerts"
             :subtext="formatFailedLogins(healthData.security.failed_logins_in_range)"
             icon-name="heroicons:shield-exclamation"
             icon-color="text-red-500"
             bg-color="bg-red-50 dark:bg-red-900/10"
          />
        </div>
      </section>

      <div class="border-t border-slate-200 dark:border-slate-700 my-8 opacity-50"></div>

      <!-- Section 2: Cost Analytics -->
      <section class="space-y-8">
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center gap-2">
            <div class="h-8 w-1 bg-purple-500 rounded-full"></div>
            <h2 class="text-2xl font-bold text-slate-800 dark:text-white">Cost Analytics</h2>
          </div>
          <!-- Page-level "Updated X ago" lives in the header — no per-section
               duplicate here. -->
        </div>

        <!-- Cost Overview Cards -->
        <CostOverviewCards v-if="overviewData" :data="overviewData" />

        <!-- Cost Trend & Warehouse Breakdown. Stretching children so the
             two panels share a row height — Cost By Warehouse internally
             fills with a scrollable table; Cost Trend fills with chart
             whitespace. Cost Trend uses the shared ConsumptionCostChart
             (same component as the access-control pages' cost charts)
             so users get the chart-type / stacked / fill / top-N
             options menu and CSV export on the overview as well. The
             wide-form trend payload is pivoted into long-form rows
             keyed on cost category (`Compute`, `Storage`, `Data
             Transfer`, `Cloud Services`) — see `trendCostRows` below. -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          <ConsumptionCostChart
            v-if="trendData"
            chart-id="overview-cost-trend"
            title="Cost Trend"
            description="Daily spend split by cost category (compute + cloud services credits, storage day-prorated, data-transfer egress)."
            entity-label-singular="category"
            entity-label-plural="categories"
            :rows="trendCostRows"
          />
          <CostByWarehouse v-if="warehouseData" :warehouses="warehouseData.warehouses" />
        </div>

        <!-- Recommendations & Anomalies -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <CostRecommendations
            v-if="recommendationsData"
            :recommendations="recommendationsData.recommendations"
            @action="handleRecAction"
          />
          <!-- Anomalies filtered through the notifications store's
               per-user dismissed set so a swiped-away anomaly stays
               hidden across page refreshes. The Snowflake fetch
               returns the full anomaly list every time (it's just an
               aggregate over METERING_DAILY_HISTORY); persisted
               dismissals live on the per-user `dismissedBy` array of
               the corresponding notification doc. -->
          <CostAnomalies
            v-if="anomaliesData"
            :anomalies="visibleAnomalies"
            @investigate="handleAnomInvestigate"
            @dismiss="handleAnomDismiss"
          />
        </div>

        <!-- Usage Patterns (Heatmap) -->
        <div v-if="heatmapData" class="space-y-4">
          <div class="flex items-center gap-2">
            <Icon name="heroicons:calendar" class="w-5 h-5 text-purple-500" />
            <h3 class="text-lg font-semibold text-slate-800 dark:text-white">Usage Patterns</h3>
          </div>
          <CostHeatmap
            :data="heatmapData.data"
            @cell-click="handleHeatmapClick"
          />
        </div>

        <!-- User Spend Table -->
        <div v-if="userData" class="space-y-4">
          <div class="flex items-center gap-2">
            <Icon name="heroicons:users" class="w-5 h-5 text-purple-500" />
            <h3 class="text-lg font-semibold text-slate-800 dark:text-white">User Spend Breakdown</h3>
          </div>
          <CostByUserTable
            :users="userData.users"
            @user-click="handleUserClick"
          />
        </div>
      </section>
    </div>

    <!-- Anomaly investigation drawer. Mounted at the page root rather
         than inside the section so its Teleported-to-body overlay doesn't
         have to hop out of any nested clipping. Driven by the
         `investigatingAnomaly` state — set when the user clicks
         "Investigate" on a Cost Anomalies row, cleared on close. -->
    <AnomalyInvestigationDrawer
      v-if="investigatingAnomaly"
      :anomaly="investigatingAnomaly"
      @close="investigatingAnomaly = null"
    />
  </div>
</template>

<script setup lang="ts">
import KPICard from '~/components/dashboard/KPICard.vue'
import CostOverviewCards from '~/components/cost/CostOverviewCards.vue'
import ConsumptionCostChart, { type ConsumptionCostRow } from '~/components/dashboard/ConsumptionCostChart.vue'
import CostByWarehouse from '~/components/cost/CostByWarehouse.vue'
import AnomalyInvestigationDrawer from '~/components/cost/AnomalyInvestigationDrawer.vue'
import type { CostAnomaly } from '~/scripts/anomalyRecommendations'
import CostByUserTable from '~/components/cost/CostByUserTable.vue'
import CostAnomalies from '~/components/cost/CostAnomalies.vue'
import CostRecommendations from '~/components/cost/CostRecommendations.vue'
import CostHeatmap from '~/components/cost/CostHeatmap.vue'
import DashboardSkeleton from '~/components/dashboard/DashboardSkeleton.vue'
import DashboardFilterBar from '~/components/dashboard/DashboardFilterBar.vue'
import { DEFAULT_TTL_MS, useSwrCache } from '~/composables/useSwrCache'
import { dateRangeCacheKey, formatDateRangeLabel } from '~/composables/useDashboardFilters'

// 💬 Setup-state derivation drives whether to show the "continue
// setup" CTA or the dashboard scaffolding while data loads.
//
// `setupReady` flips true once the account profile has hydrated
// from Firestore — without it, a fresh page load would flash the
// CTA before reading the doc, then disappear it once setupProgress
// arrives. We treat any truthy `accountProfile` as "we've heard from
// Firestore"; the doc always carries `id` even on a brand-new account
// since `app_register_account` writes it synchronously.
const { accountProfile, snowflakeSession } = storeToRefs(useGlobalsStore())
const setupReady = computed(() => !!(accountProfile.value as any)?.id)
const setupComplete = computed(() => {
  const sp = (accountProfile.value as any)?.setupProgress || {}
  return sp.personalAuth?.status === 'complete'
    && sp.systemRole?.status === 'complete'
    && sp.systemUser?.status === 'complete'
    && sp.engineInstall?.status === 'complete'
})

// Cost Analytics fetchers (real Snowflake queries via useCostData). All
// time-bound fetchers accept the dashboard's active DateRangeArg so the
// cards reflect whatever range the user has selected in DashboardFilterBar.
const {
  fetchOverview,
  fetchTrend,
  fetchByWarehouse,
  fetchByUser,
  fetchAnomalies,
  fetchRecommendations,
  fetchHeatmap,
} = useCostData()

// System Health fetcher — replaces the prior /api/mocks/snowflake/health
// stub with five real sub-queries: CURRENT_VERSION probe (live), credits
// over the active range, currently-active queries + today's count (live),
// failed logins over the active range, and currently long-running queries.
// Range-bound metrics (credits, failed logins) follow DashboardFilterBar;
// real-time metrics ignore the range by design.
const { fetchHealth } = useHealthData()

// Dashboard filters — only the date range is enabled here. Other dashboards
// (cost-analytics, query-history, etc.) can opt into more by passing
// `warehouses: true`, `users: true`, etc. when they call this composable.
const filters = useDashboardFilters({ dateRange: true, defaultDateRange: 'last_30d' })

// 🔔 Notification sync. Each loadAll() pass writes any new anomalies +
// long-running queries to the per-account notifications subcollection,
// where the bell / toast / page surfaces all pick them up via the
// Firestore listener.
const notificationSync = useNotificationSync()

// Friendly label for the active range — interpolated into the time-bound
// KPI card titles ("Credits Used (Last 30 days)", "Security Alerts (Today)")
// so the cards self-describe what window they're showing.
const rangeLabel = computed(() => formatDateRangeLabel(filters.state.dateRange))

// SWR cache, scoped per Snowflake account so users on different accounts
// (or after a re-auth) don't see each other's numbers.
const cache = useSwrCache()
const { snowflakeCredentials } = storeToRefs(useSnowflakeAuthStore())
const sfId = computed(() => snowflakeCredentials.value?.sfId || null)

// One ref per dashboard section. Each holds { data, ageMs } where
// `ageMs` powers the "Updated X ago" indicator. Loading is tracked at
// the page level (single `refreshing` flag) — sections don't flip into
// individual loading states, so the cached content stays visible while
// fresh data is fetched in the background.
function makeSection<T = any>() {
  return reactive({ data: null as T | null, ageMs: null as number | null })
}
const health = makeSection()
const overview = makeSection()
const trend = makeSection()
const warehouse = makeSection()
const user = makeSection()
const anomalies = makeSection()
const recommendations = makeSection()
const heatmap = makeSection()

interface Section {
  ref: ReturnType<typeof makeSection>
  fetch: () => Promise<any>
  cacheKey: string
}
/**
 * Build the section list with closures bound to the CURRENT filter values.
 * Called fresh on each loadAll so the date-range fetchers receive the
 * latest range, and the cache keys include the range so switching back to
 * a previously-viewed range hits cache instantly.
 *
 * Every visible card on this dashboard is now range-aware: health credits
 * + failed-logins, the overview KPI cards, trend, warehouse, user,
 * anomalies, recommendations, and the heatmap. The only metrics that
 * remain unfiltered are intrinsically real-time (connection latency, the
 * "currently active" query count) — which the health composable handles
 * internally without consulting the range.
 */
function buildSections(): Section[] {
  const range = filters.state.dateRange
  const r = dateRangeCacheKey(range)
  const arg = { start: range.start, end: range.end }
  return [
    { ref: health,          fetch: () => fetchHealth(arg),          cacheKey: cache.buildKey('overview', `health:${r}`) },
    { ref: overview,        fetch: () => fetchOverview(arg),        cacheKey: cache.buildKey('overview', `cost-overview:${r}`) },
    { ref: trend,           fetch: () => fetchTrend(arg),           cacheKey: cache.buildKey('overview', `cost-trend:${r}`) },
    { ref: warehouse,       fetch: () => fetchByWarehouse(arg),     cacheKey: cache.buildKey('overview', `cost-by-warehouse:${r}`) },
    { ref: user,            fetch: () => fetchByUser(arg),          cacheKey: cache.buildKey('overview', `cost-by-user:${r}`) },
    { ref: anomalies,       fetch: () => fetchAnomalies(arg),       cacheKey: cache.buildKey('overview', `cost-anomalies:${r}`) },
    { ref: recommendations, fetch: () => fetchRecommendations(arg), cacheKey: cache.buildKey('overview', `cost-recommendations:${r}`) },
    { ref: heatmap,         fetch: () => fetchHeatmap(arg),         cacheKey: cache.buildKey('overview', `cost-heatmap:${r}`) },
  ]
}

// Per-template aliases — the existing markup binds to these names.
const healthData = computed(() => health.data)
const overviewData = computed(() => overview.data)
const trendData = computed(() => trend.data)
/**
 * Pivot the wide-form trend payload (one row per day with four cost
 * columns) into the long-form `{ day, entity, cost }` shape that
 * ConsumptionCostChart expects. `entity` is the cost category — keeps
 * Compute / Cloud Services as separate stacks since their drivers and
 * remediations differ (warehouse over-provisioning vs. cloud-services
 * spend). Zero-cost entries are emitted too so the chart's day axis
 * stays continuous even on days where a category was idle.
 */
const trendCostRows = computed<ConsumptionCostRow[]>(() => {
  const rows = trendData.value?.data
  if (!rows?.length) return []
  const out: ConsumptionCostRow[] = []
  for (const r of rows) {
    out.push({ day: r.date, entity: 'Compute',        cost: r.computeCost })
    out.push({ day: r.date, entity: 'Cloud Services', cost: r.cloudServicesCost })
    out.push({ day: r.date, entity: 'Storage',        cost: r.storageCost })
    out.push({ day: r.date, entity: 'Data Transfer',  cost: r.dataTransferCost })
  }
  return out
})
const warehouseData = computed(() => warehouse.data)
const userData = computed(() => user.data)
const anomaliesData = computed(() => anomalies.data)
const recommendationsData = computed(() => recommendations.data)
const heatmapData = computed(() => heatmap.data)

// Page-level refresh flag — the banner watches this. We avoid flipping
// per-section loading states so the cached UI doesn't churn while a
// background refresh is in flight.
const refreshing = ref(false)
// Captured at the start of each load (after cache-paint, before fetch)
// and frozen for the duration of the load. Drives the banner's copy:
//   - true  → "Refreshing data — current view stays…"  (warm refresh)
//   - false → "Loading data from Snowflake…"           (cold first load)
// Without this lock, the banner would flip mid-load as the first
// section's data lands during cold progressive reveal.
const hadDataAtRefreshStart = ref(false)
// Sections refs are stable; only the closures + cache keys vary by filter
// state. So `hasAnyData` / `oldestAgeMs` can read directly from the ref
// objects regardless of the current filter pass.
const allSectionRefs = [
  health, overview, trend, warehouse, user,
  anomalies, recommendations, heatmap,
]
const hasAnyData = computed(() => allSectionRefs.some(s => s.data !== null))
const oldestAgeMs = computed(() => {
  const ages = allSectionRefs.map(s => s.ageMs).filter((a): a is number => a !== null)
  return ages.length ? Math.max(...ages) : null
})

/// Stale-while-revalidate over every section.
///   1. Paint cache hits immediately (so navigation feels instant).
///   2. If everything is fresh per TTL and we aren't forcing, we're done.
///   3. Otherwise fire all fetches in parallel.
///      - On a WARM load (we have cached data on screen, or this is a
///        forced refresh after data was already shown): hold the new
///        results in a buffer and apply them ATOMICALLY at the end so
///        the cached view doesn't churn while fetches are in flight.
///      - On a COLD load (nothing on screen yet): write each section as
///        it resolves so users see content as fast as possible. The
///        skeleton (`v-else-if="!healthData"`) hides as soon as health
///        lands; remaining sections fade in progressively.
async function loadAll(opts: { force?: boolean } = {}) {
  // Build the section list bound to the CURRENT filter values. Reading
  // a fresh list every load keeps cache keys + fetch closures in lock-
  // step with whatever the user has selected.
  const sections = buildSections()

  // Step 1: cache paint. Skip if forcing — caller wants raw fresh data.
  if (!opts.force) {
    for (const s of sections) {
      const cached = cache.get<any>(s.cacheKey, { sfId: sfId.value, ttlMs: DEFAULT_TTL_MS })
      if (cached) {
        s.ref.data = cached.data
        s.ref.ageMs = cached.ageMs
      }
    }
  }

  // Step 2: short-circuit if every section's cache is fresh.
  if (!opts.force) {
    const allFresh = sections.every(s => {
      const cached = cache.get<any>(s.cacheKey, { sfId: sfId.value, ttlMs: DEFAULT_TTL_MS })
      return cached?.isFresh
    })
    if (allFresh) return
  }

  // Step 3: don't fire Snowflake queries before the session is usable.
  // useCostData calls go through snowflakeAuth which logs noisy "no
  // usable refresh token" warnings if the session isn't ready (fresh
  // signup race, hard refresh before accountProfile hydrates, partial
  // login state). The watcher below re-runs loadAll when the session
  // lands.
  if (!snowflakeSession.value?.user) return

  // Decide swap strategy. If anything is already on screen (cache hit
  // OR a previous loadAll filled the sections), hold new results and
  // apply atomically. Otherwise progressive reveal.
  const isWarm = sections.some(s => s.ref.data !== null)

  // Lock the banner copy for the duration of this load. We read
  // `isWarm` here once instead of binding to `hasAnyData` reactively
  // — that prevents the banner text from flipping mid-load when the
  // first section's data lands during cold progressive reveal.
  hadDataAtRefreshStart.value = isWarm
  refreshing.value = true
  try {
    if (isWarm) {
      // Atomic swap: collect results, then commit them all at once.
      const results = await Promise.allSettled(
        sections.map(async (s) => ({ section: s, fresh: await s.fetch() })),
      )
      for (const r of results) {
        if (r.status === 'fulfilled') {
          const { section, fresh } = r.value
          section.ref.data = fresh
          section.ref.ageMs = 0
          cache.set(section.cacheKey, fresh, { sfId: sfId.value })
        } else {
          // Keep stale data on screen on per-section failure — no
          // section gets blanked just because its query blew up. Log
          // for the dev console so we can investigate.
          console.error('[overview] section refresh failed:', r.reason)
        }
      }
    } else {
      // Progressive reveal during cold load.
      await Promise.allSettled(sections.map(async (s) => {
        try {
          const fresh = await s.fetch()
          s.ref.data = fresh
          s.ref.ageMs = 0
          cache.set(s.cacheKey, fresh, { sfId: sfId.value })
        } catch (err: any) {
          console.error(`[overview] section ${s.cacheKey} failed:`, err)
        }
      }))
    }
  } finally {
    refreshing.value = false
  }

  // 🔔 Notification sync — write any newly-detected anomalies and
  // long-running queries into the Firestore notifications collection
  // so the bell + toast surfaces pick them up. Idempotent (stable doc
  // ids), so calling on every load doesn't duplicate. Wrapped in a
  // try/catch so a transient Firestore write hiccup doesn't break
  // the cost dashboard render.
  try {
    if (anomaliesData.value?.anomalies?.length) {
      notificationSync.syncCostAnomalies(anomaliesData.value.anomalies)
    }
    if (healthData.value?.queries?.long_running?.length) {
      notificationSync.syncLongRunningQueries(healthData.value.queries.long_running)
    }
  } catch (err) {
    console.warn('[overview] notification sync failed:', err)
  }
}

/** Refresh button → bypass cache, hit Snowflake on every section. */
function refreshAll() {
  return loadAll({ force: true })
}

// Re-run when the Snowflake session lands (StatusIndicator's probe flips
// it from null → populated). A user who lands here before the session
// is ready will paint the skeleton, then loadAll runs once the watcher
// fires.
watch(snowflakeSession, (next) => {
  if (next?.user && !hasAnyData.value) loadAll()
}, { immediate: false })

// Re-load whenever the date range changes. We watch the presetId + the
// start/end timestamps so both preset clicks and custom-range Apply
// triggers fire the reload. SWR cache keys incorporate the range, so
// switching back to a previously-viewed range hits cache instantly.
watch(
  () => [filters.state.dateRange.presetId, filters.state.dateRange.start.getTime(), filters.state.dateRange.end.getTime()],
  () => loadAll(),
)

// Initial load — kicks the cache paint immediately. If the session is
// ready, also fires fetches. If not, the watcher will pick up later.
loadAll()

// Tick the age indicator once a minute so "X minutes ago" stays current
// without per-section data churn. Walks the stable `allSectionRefs` list
// directly — `sections` (built inside loadAll) is no longer module-scoped
// after the filter refactor.
// Auto-refresh threshold: if the oldest cached section is more than 1 day
// old, trigger a forced refresh so users never see badly stale data.
const AUTO_REFRESH_TTL_MS = 24 * 60 * 60 * 1000

function checkAutoRefresh() {
  if (refreshing.value) return
  const age = oldestAgeMs.value
  if (age !== null && age > AUTO_REFRESH_TTL_MS) {
    console.info('[overview] data is >1 day old — auto-refreshing')
    refreshAll()
  }
}

let ageInterval: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  ageInterval = setInterval(() => {
    allSectionRefs.forEach((s) => { if (s.ageMs !== null) s.ageMs += 60_000 })
  }, 60_000)

  // Auto-refresh on tab focus after the page has been hidden. Handles the
  // common case of a user leaving the overview open overnight and returning
  // to find stale data.
  document.addEventListener('visibilitychange', checkAutoRefresh)

  // Also check immediately on mount in case the cache is already stale
  // (e.g. a page reload after the browser was left open overnight).
  checkAutoRefresh()
})
onBeforeUnmount(() => {
  if (ageInterval) clearInterval(ageInterval)
  document.removeEventListener('visibilitychange', checkAutoRefresh)
})

const formatCurrency = (val: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)
}

// Credits often come back from Snowflake with long fractional tails
// (e.g. 14.705925393). Round to 1 decimal for the headline KPI.
const formatCredits = (val: number) => {
  if (typeof val !== 'number' || !isFinite(val)) return '0'
  return val.toFixed(1)
}

// Compact number for "X queries today" subtext. This stays "today"-anchored
// because the underlying counter is a real-time COUNT_IF on START_TIME >=
// CURRENT_DATE() — it doesn't move with the filter. The Active Queries card
// is intentionally exempt from the range; the time-bound cards (Credits,
// Security) are the ones that follow the filter.
const formatTotalToday = (val: number) => {
  const n = Number(val) || 0
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k Total Today`
  return `${n} Total Today`
}

// Failed-logins subtext on the Security Alerts card. The card's title
// already shows "(rangeLabel)", so the subtext just disambiguates count
// and pluralization without redundantly repeating the timeframe.
const formatFailedLogins = (val: number) => {
  const n = Number(val) || 0
  if (n === 0) return 'No failed logins'
  return `${n} failed login${n === 1 ? '' : 's'}`
}

function formatRelativeAge(ms: number): string {
  if (ms < 60_000) return 'just now'
  const min = Math.floor(ms / 60_000)
  if (min < 60) return `${min} min ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`
  const day = Math.floor(hr / 24)
  return `${day} day${day === 1 ? '' : 's'} ago`
}

// Anomaly investigation drawer state. The drawer fetches its own
// (warehouse, day)-scoped drill-down data and runs the rule-based
// recommendations engine — this page just toggles which anomaly the
// drawer is focused on. Cleared when the drawer closes.
const investigatingAnomaly = ref<CostAnomaly | null>(null)

// Event handlers for cost components
const handleAnomInvestigate = (anomaly: CostAnomaly) => {
  investigatingAnomaly.value = anomaly
}

// 🔔 Deep-link from notifications: a notification click navigates to
// /dashboards/overview?anomaly=<id>. As soon as the matching anomaly
// row lands in `anomaliesData`, we open the investigation drawer for
// it. Watching `anomaliesData` rather than just doing it on mount
// covers the cold-load case where the user lands on the page before
// the SWR cache has any data.
const route = useRoute()
const router = useRouter()
watch(
  () => [route.query.anomaly, anomaliesData.value?.anomalies?.length],
  () => {
    // `route.query.anomaly` can theoretically be an array if the URL
    // has `?anomaly=a&anomaly=b`. Normalize to first string so the
    // .find() below has a stable scalar to compare against.
    const raw = route.query.anomaly
    const target = Array.isArray(raw) ? raw[0] : raw
    if (!target || investigatingAnomaly.value) return
    const list = anomaliesData.value?.anomalies || []
    const match = list.find((a: any) => a.id === target)
    if (match) {
      investigatingAnomaly.value = match as CostAnomaly
      // Strip the query param so a refresh / back-nav doesn't re-open
      // the drawer ad infinitum. Replace (no history entry) so the
      // user's back button still goes to /notifications.
      router.replace({ path: route.path, query: {} })
    }
  },
  { immediate: true },
)
// 🔔 Pull the per-user dismissed-notification ID set so we can hide
// any anomaly the user has already swiped away. The set is populated
// by the notifications store's onSnapshot listener, so it stays in
// sync across tabs / devices without us doing anything special.
const { dismissedIds: dismissedNotificationIds, dismiss: dismissNotification } = useNotifications()

/**
 * Visible anomalies = full Snowflake-fetched list minus any whose
 * matching notification (same doc id) the user has dismissed. We use
 * `anomalyId` first because the sync layer writes notifications with
 * the anomaly's own stable id (`anom_<warehouse>_YYYYMMDD`); fall
 * back to `id` for safety.
 */
const visibleAnomalies = computed(() => {
  const all = anomaliesData.value?.anomalies || []
  if (!dismissedNotificationIds.value.size) return all
  return all.filter((a: any) =>
    !dismissedNotificationIds.value.has(a.id),
  )
})

/**
 * Persist the dismissal through the notifications store so it
 * survives a page refresh / Snowflake re-fetch. The notification doc
 * id matches the anomaly id (set by the sync layer), so calling
 * `dismiss(anomalyId)` adds the current user to its `dismissedBy`
 * array. The listener immediately reflects that into
 * `dismissedNotificationIds`, which `visibleAnomalies` reads — so
 * the row disappears synchronously after the click.
 *
 * Edge case: if the sync hasn't created the notification doc yet
 * (race on a freshly-loaded page), the dismiss call will create it
 * with just the `dismissedBy` array, and the next sync pass merges
 * the rest of the fields onto it. Either order is safe.
 */
const handleAnomDismiss = (anomalyId: string) => {
  if (!anomalyId) return
  void dismissNotification(anomalyId)
}
const handleRecAction = (recommendation: any) => alert(`Action: ${recommendation.action}\nPotential Savings: $${recommendation.potentialSavings}`)
const handleUserClick = (user: any) => console.log('User clicked:', user)
const handleHeatmapClick = (cellData: any) => console.log('Heatmap cell clicked:', cellData)
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 20px;
}
</style>
